export class Memberships {
    currentActiveLevel: string = '';
    membershipLevels: NodeListOf<Element>;
    benefitElements: NodeListOf<Element>;
    hiddenInput: HTMLInputElement | null;
    activeLevelElement: HTMLElement | null;
    activePriceElement: HTMLElement | null;
    minicartElement: HTMLElement | null;
    levelsContainerElement: HTMLElement | null;
    levelsObserver: IntersectionObserver | null;
    pageWrapperElement: HTMLElement | null;
    isLeadersCirclePage: boolean = false;
    declineFairMarketCheckbox: HTMLInputElement | null;
    declineBenefitsHiddenInput: HTMLInputElement | null;
    taxDeductibleElement: HTMLElement | null;
    upgradeButtons: NodeListOf<Element> | null;

    // Donation-specific properties
    isDonationPage: boolean = false;
    customAmountRadio: HTMLInputElement | null = null;
    customAmountInput: HTMLInputElement | null = null;
    amountRadios: NodeListOf<HTMLInputElement> | null = null;
    donationForm: HTMLFormElement | null = null;

    constructor() {
        this.membershipLevels = document.querySelectorAll('[data-membership-level]');
        this.benefitElements = document.querySelectorAll('[data-enabled-levels]');
        this.hiddenInput = document.getElementById('hdnMembershipLevelName') as HTMLInputElement;
        this.activeLevelElement = document.querySelector('[data-active-level]') as HTMLElement;
        this.activePriceElement = document.querySelector('[data-active-price]') as HTMLElement;
        this.minicartElement = document.querySelector('[data-membership-minicart]') as HTMLElement;
        this.levelsContainerElement = document.querySelector('.membership-levels-container') as HTMLElement;
        this.pageWrapperElement = document.querySelector('.membership-page-wrapper') as HTMLElement;
        this.upgradeButtons = document.querySelectorAll('[data-upgrade-level]');
        this.init();
    }

    init() {
        this.initLeadersCircleElements();
        this.initDonationElements();
        this.activateListeners();
        this.initializeDefaultLevel();
        this.initIntersectionObserver();
        this.setMinicartHeight();
    }

    initLeadersCircleElements() {
        const leadersContainer = document.querySelector('[data-membership-leaders="true"]');
        this.isLeadersCirclePage = leadersContainer !== null;

        if (this.isLeadersCirclePage) {
            this.declineFairMarketCheckbox = document.getElementById('declineFairMarketValue') as HTMLInputElement;
            this.declineBenefitsHiddenInput = document.getElementById('hdnDeclineBenefits') as HTMLInputElement;
            this.taxDeductibleElement = document.querySelector('[data-tax-deductible-amount]') as HTMLElement;
        }
    }

    initDonationElements() {
        // Check if we're on a donation page
        this.customAmountRadio = document.getElementById('Amounts_custom') as HTMLInputElement;
        this.customAmountInput = document.getElementById('Amounts_custom-field') as HTMLInputElement;
        this.amountRadios = document.querySelectorAll('input[name="MembershipRequest.Amount"]') as NodeListOf<HTMLInputElement>;
        this.donationForm = document.querySelector('form') as HTMLFormElement;
        
        this.isDonationPage = !!(this.customAmountRadio && this.customAmountInput);
    }

    activateListeners() {
        this.membershipLevels.forEach(level => {
            level.addEventListener('click', (event) => {
                this.handleLevelClick(event.currentTarget as Element);
            });
        });

        this.upgradeButtons.forEach(btn => {
            btn.addEventListener('click', (event) => {
                let level = (event.currentTarget as Element).getAttribute('data-upgrade-level');
                this.setActiveLevel(level);
                this.pageWrapperElement?.scrollIntoView();
            });
        });

        if (this.isLeadersCirclePage && this.declineFairMarketCheckbox) {
            this.declineFairMarketCheckbox.addEventListener('change', () => {
                this.handleDeclineFairMarketChange();
            });
        }

        // Initialize donation-specific listeners
        if (this.isDonationPage) {
            this.initDonationListeners();
        }
    }

    initDonationListeners() {
        if (!this.customAmountInput || !this.customAmountRadio) return;

        // Handle custom amount input
        this.customAmountInput.addEventListener('input', () => {
            this.handleCustomAmountInput();
        });

        this.customAmountInput.addEventListener('focus', () => {
            this.handleCustomAmountFocus();
        });

        // Handle form submission
        if (this.donationForm) {
            this.donationForm.addEventListener('submit', (e) => {
                this.handleDonationFormSubmit(e);
            });
        }

        // Clear custom amount when other amounts are selected
        if (this.amountRadios) {
            this.amountRadios.forEach((radio) => {
                if (radio.id !== 'Amounts_custom') {
                    radio.addEventListener('change', () => {
                        if (radio.checked && this.customAmountInput) {
                            this.customAmountInput.value = '';
                        }
                    });
                }
            });
        }
    }

    handleCustomAmountInput() {
        if (!this.customAmountRadio || !this.customAmountInput) return;

        // Select the custom radio when user types in the custom amount field
        if (this.customAmountInput.value.trim() !== '') {
            this.customAmountRadio.checked = true;
            this.customAmountRadio.value = this.customAmountInput.value;
        }
    }

    handleCustomAmountFocus() {
        if (!this.customAmountRadio) return;
        
        // Select custom radio when focusing on the input
        this.customAmountRadio.checked = true;
    }

    handleDonationFormSubmit(e: Event) {
        if (!this.customAmountRadio || !this.customAmountInput) return;

        if (this.customAmountRadio.checked) {
            const customValue = this.customAmountInput.value.trim();
            const numericValue = parseFloat(customValue.replace(/[,$]/g, ''));
            
            if (customValue === '' || isNaN(numericValue) || numericValue <= 0) {
                e.preventDefault();
                this.showAmountError();
                return false;
            } else {
                // Update the radio button value with the custom amount
                this.customAmountRadio.value = numericValue.toString();
                this.hideAmountError();
            }
        }
        return true;
    }

    showAmountError() {
        const errorElement = document.querySelector('[data-amount-invalid-error]') as HTMLElement;
        if (errorElement) {
            errorElement.removeAttribute('hidden');
        }
    }

    hideAmountError() {
        const errorElement = document.querySelector('[data-amount-invalid-error]') as HTMLElement;
        if (errorElement) {
            errorElement.setAttribute('hidden', '');
        }
    }

    handleDeclineFairMarketChange() {
        if (!this.declineFairMarketCheckbox || !this.declineBenefitsHiddenInput) return;
        this.declineBenefitsHiddenInput.value = this.declineFairMarketCheckbox.checked ? 'true' : 'false';
    }

    initIntersectionObserver() {
        if (!this.minicartElement || !this.levelsContainerElement) {
            return;
        }

        this.minicartElement.classList.add('hidden');

        this.levelsObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this.showMinicart();
                    } else {
                        this.hideMinicart();
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -100px 0px'
            }
        );

        this.levelsObserver.observe(this.levelsContainerElement);
    }

    setMinicartHeight() {
        if (this.minicartElement) {
            requestAnimationFrame(() => {
                const height = this.minicartElement!.offsetHeight;
                document.documentElement.style.setProperty('--minicart-height', `${height}px`);
            });
        }
    }

    showMinicart() {
        if (!this.minicartElement) return;

        this.minicartElement.classList.remove('hidden');
        this.minicartElement.classList.add('visible');

        if (this.pageWrapperElement) {
            this.pageWrapperElement.classList.add('minicart-active');
        }

        this.setMinicartHeight();
    }

    hideMinicart() {
        if (!this.minicartElement) return;

        this.minicartElement.classList.remove('visible');
        this.minicartElement.classList.add('hidden');

        if (this.pageWrapperElement) {
            this.pageWrapperElement.classList.remove('minicart-active');
        }
    }

    handleLevelClick(levelElement: Element) {
        const membershipLevel = levelElement.getAttribute('data-membership-level');
        const membershipPrice = levelElement.getAttribute('data-membership-price');
        const deductibleAmount = levelElement.getAttribute('data-level-deductible-amount');
        const membershipDisplayName = levelElement.getAttribute('data-membership-display-name');
        if (!membershipLevel) return;

        this.membershipLevels.forEach(level => {
            level.classList.remove('active');
        });

        levelElement.classList.add('active');

        this.currentActiveLevel = membershipLevel;

        if (this.hiddenInput) {
            this.hiddenInput.value = membershipLevel;
        }

        this.updateActiveLevelDisplay(membershipDisplayName, membershipPrice, deductibleAmount);
        this.updateBenefitVisibility();
    }

    updateActiveLevelDisplay(levelName: string,  price: string | null, deductibleAmount: string | null) {
        if (this.activeLevelElement) {
            this.activeLevelElement.textContent = levelName.charAt(0).toUpperCase() + levelName.slice(1);
        }

        if (this.activePriceElement && price) {
            this.activePriceElement.textContent = price;
        }
            
        if (this.taxDeductibleElement && deductibleAmount) {
            this.taxDeductibleElement.textContent = deductibleAmount;
        }
    }

    updateBenefitVisibility() {
        this.benefitElements.forEach(benefit => {
            const enabledLevels = benefit.getAttribute('data-enabled-levels');

            if (enabledLevels && this.currentActiveLevel) {
                const isEnabled = enabledLevels.includes(this.currentActiveLevel);

                if (isEnabled) {
                    benefit.classList.add('active');
                } else {
                    benefit.classList.remove('active');
                }
            }
        });
    }

    getQueryStringLevel() {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('level');
    }

    initializeDefaultLevel() {
        const queryStringLevel = this.getQueryStringLevel();

        if (queryStringLevel) {
            const queryLevelElement = document.querySelector(`[data-membership-level="${queryStringLevel.toLowerCase()}"]`);
            if (queryLevelElement) {
                this.handleLevelClick(queryLevelElement);
                return;
            }
        }

        const defaultLevel = this.hiddenInput?.value || 'contributor';
        const defaultLevelElement = document.querySelector(`[data-membership-level="${defaultLevel.toLowerCase()}"]`);

        if (defaultLevelElement) {
            this.handleLevelClick(defaultLevelElement);
        }
        else if (this.membershipLevels.length > 0) {
            this.handleLevelClick(this.membershipLevels[0]);
        }
    }

    getCurrentActiveLevel() {
        return this.currentActiveLevel;
    }

    setActiveLevel(levelName: string) {
        const levelElement = document.querySelector(`[data-membership-level="${levelName.toLowerCase()}"]`);
        if (levelElement) {
            this.handleLevelClick(levelElement);
        }
    }

    destroy() {
        if (this.levelsObserver) {
            this.levelsObserver.disconnect();
        }

        if (this.pageWrapperElement) {
            this.pageWrapperElement.classList.remove('minicart-active');
        }

        document.documentElement.style.removeProperty('--minicart-height');
    }
}

new Memberships();


